import { DateTime } from "luxon";
import { DateHelper } from "../../gyzmo-commons/helpers/date.helper";
import { DATE_NODEJS_FORMAT } from "../../gyzmo-commons/interfaces/constants";
import { Movement } from "../models/movement.model";
import { BaseDto } from "./base/base.dto";
import { EquipmentDto } from "./equipment.dto";
import { LocationDto } from "./location.dto";
import { ModelDto } from "./model.dto";
import { ServiceDto } from "./service.dto";
import { ThirdPartyDto } from "./thirdParty.dto";

export class MovementDto extends BaseDto<Movement> {
    id: string = "";
    documentNumber: string = "";
    contractNumber: string = "";
    startDate: DateTime = null;
    returnDate: DateTime = null;
    returnFuel: number = 0;
    returnFuel2: number = 0;
    returnMileage: number = 0;
    returnMileage2: number = 0;
    startFuel: number = 0;
    startFuel2: number = 0;
    startMileage: number = 0;
    startMileage2: number = 0;
    isClosed: boolean = false;
    customer: ThirdPartyDto = null;
    driver: ThirdPartyDto = null;
    startLocation: LocationDto;
    returnLocation: LocationDto;
    relatedTable: string = null;

    position: { id: string } = { id: null };

    model: ModelDto = null;
    service: ServiceDto = null;

    // N'appartient pas au model, mais sert lors de la création d'un départ/retour sur tablette
    // N'est pas hydraté, c'est toujours un EquipmentBase
    equipment: EquipmentDto = null;

    constructor() {
        super();

        this.customer = new ThirdPartyDto();
        this.driver = new ThirdPartyDto();
        this.model = new ModelDto();
        this.service = new ServiceDto();
        this.startLocation = new LocationDto();
        this.returnLocation = new LocationDto();
    }

    public static fromBody(body: any): MovementDto {
        let movementDto = new MovementDto();

        movementDto.id = body.id;
        movementDto.documentNumber = body.documentNumber;
        movementDto.contractNumber = body.contractNumber;

        movementDto.startDate = DateHelper.tryFromFormat(body.startDate, DATE_NODEJS_FORMAT);
        movementDto.returnDate = DateHelper.tryFromFormat(body.returnDate, DATE_NODEJS_FORMAT);
        movementDto.returnFuel = body.returnFuel;
        movementDto.returnFuel2 = body.returnFuel2;
        movementDto.returnMileage = body.returnMileage;
        movementDto.returnMileage2 = body.returnMileage2;
        movementDto.startFuel = body.startFuel;
        movementDto.startFuel2 = body.startFuel2;
        movementDto.startMileage = body.startMileage;
        movementDto.startMileage2 = body.startMileage2;
        movementDto.isClosed = body.isClosed;

        if (body.position) {
            movementDto.position = body.position;
        }

        if (body.relatedTables) {
            movementDto.relatedTable = body.relatedTables.wording;
        }

        if (body.customer) {
            movementDto.customer = ThirdPartyDto.fromBody(body.customer);
        }

        if (body.driver) {
            movementDto.driver = ThirdPartyDto.fromBody(body.driver);
        }

        if (body.equipment) {
            movementDto.equipment = EquipmentDto.fromBody(body.equipment);
        }

        if (body.model) {
            movementDto.model = ModelDto.fromBody(body.model);
        }

        if (body.startLocation) {
            movementDto.startLocation = LocationDto.fromBody(body.startLocation);
        }

        if (body.returnLocation) {
            movementDto.returnLocation = LocationDto.fromBody(body.returnLocation);
        }

        if (body.service) {
            movementDto.service = ModelDto.fromBody(body.service);
        }

        return movementDto;
    }

    static fromModel(movement: Movement): MovementDto {
        let movementDto = new MovementDto();

        movementDto.id = movement.id;

        movementDto.documentNumber = movement.documentNumber;
        movementDto.contractNumber = movement.contractNumber;

        movementDto.startDate = DateHelper.tryFromISO(movement.startDate);
        movementDto.returnDate = DateHelper.tryFromISO(movement.returnDate);
        movementDto.returnFuel = movement.returnFuel;
        movementDto.returnFuel2 = movement.returnFuel2;
        movementDto.returnMileage = movement.returnMileage;
        movementDto.returnMileage2 = movement.returnMileage2;
        movementDto.startFuel = movement.startFuel;
        movementDto.startFuel2 = movement.startFuel2;
        movementDto.startMileage = movement.startMileage;
        movementDto.startMileage2 = movement.startMileage2;
        movementDto.relatedTable = movement.relatedTable;
        movementDto.position = movement.position;
        movementDto.isClosed = movement.isClosed == 1;

        movementDto.customer = ThirdPartyDto.fromModel(movement.customer);
        movementDto.driver = ThirdPartyDto.fromModel(movement.driver);
        movementDto.model = ModelDto.fromModel(movement.model);
        movementDto.startLocation = LocationDto.fromModel(movement.startLocation);
        movementDto.returnLocation = LocationDto.fromModel(movement.returnLocation);
        movementDto.service = ModelDto.fromModel(movement.service);

        return movementDto;
    }

    public toBody(): any {
        const clone: any = { ...this };

        clone.startDate = DateHelper.tryToFormat(this.startDate, DATE_NODEJS_FORMAT);
        clone.returnDate = DateHelper.tryToFormat(this.returnDate, DATE_NODEJS_FORMAT);

        clone.customer = this.customer.toBody();
        clone.driver = this.driver.toBody();
        clone.startLocation = this.startLocation.toBody();
        clone.returnLocation = this.returnLocation.toBody();

        if (this.equipment) {
            clone.equipment = this.equipment.toBody();
        }

        return clone;
    }

    toModel() {
        let movement = new Movement();

        movement.id = this.id;
        movement.documentNumber = this.documentNumber;
        movement.contractNumber = this.contractNumber;

        movement.startDate = DateHelper.tryToISO(this.startDate);
        movement.returnDate = DateHelper.tryToISO(this.returnDate);
        movement.returnFuel = this.returnFuel;
        movement.returnFuel2 = this.returnFuel2;
        movement.returnMileage = this.returnMileage;
        movement.returnMileage2 = this.returnMileage2;
        movement.startFuel = this.startFuel;
        movement.startFuel2 = this.startFuel2;
        movement.startMileage = this.startMileage;
        movement.startMileage2 = this.startMileage2;
        movement.relatedTable = this.relatedTable;
        movement.position = this.position;
        movement.isClosed = this.isClosed ? 1 : 0;

        if (this.model) {
            movement.model = this.model.toModel();
        }

        if (this.customer) {
            movement.customer = this.customer.toModel();
        }

        if (this.driver) {
            movement.driver = this.driver.toModel();
        }

        if (this.service) {
            movement.service = this.service.toModel();
        }

        if (this.startLocation) {
            movement.startLocation = this.startLocation.toModel();
        }

        if (this.returnLocation) {
            movement.returnLocation = this.returnLocation.toModel();
        }

        return movement;
    }
}

